import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import SEO from "@/components/SEO";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUSES = ["Draft", "Review", "Payment", "Submitted", "Active", "Paused", "Completed", "Cancelled"];
const OBJECTIVES = ["Website traffic", "Leads", "Sales/conversions", "App promotion", "Brand awareness", "Engagement"];
const PROVIDERS = [{ v: "google_ads", l: "Google Ads" }, { v: "meta_ads", l: "Meta Ads" }, { v: "tiktok_ads", l: "TikTok Ads" }];
const empty = { name: "", client_id: "", objective: "", provider: "", destination_url: "", youtube_url: "", target_country: "", target_countries: "", language: "", target_audience: "", daily_budget: "", total_budget: "", budget: "", start_date: "", end_date: "", headline: "", description: "", cta: "", status: "Draft", notes: "" };

export default function AdminCampaigns() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/admin/campaigns").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c }); setEditId(c.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name required"); return; }
    setLoading(true);
    const payload = { ...form, target_countries: form.target_countries ? String(form.target_countries).split(",").map((s) => s.trim()).filter(Boolean) : [] };
    try {
      if (editId) await api.put(`/admin/campaigns/${editId}`, payload);
      else await api.post("/admin/campaigns", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed to save"); } finally { setLoading(false); }
  };

  const submitToProvider = async (id) => {
    try {
      await api.post(`/admin/campaigns/${id}/submit`);
      toast.success("Submitted to provider."); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    }
  };
  const del = async (id) => { await api.delete(`/admin/campaigns/${id}`); toast.success("Deleted"); load(); };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <div>
      <SEO title="Campaigns | Admin" />
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold tracking-tight">Campaigns</h1><p className="text-slate-400 text-sm">{items.length} campaigns</p></div>
        <Button onClick={openNew} data-testid="campaign-new"><Plus className="w-4 h-4 mr-1" /> New campaign</Button>
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        {items.map((c) => (
          <div key={c.id} className="rounded-xl border border-white/10 bg-white/5 p-5">
            <div className="flex justify-between items-start gap-3">
              <div className="cursor-pointer" onClick={() => openEdit(c)}>
                <h3 className="font-display font-bold">{c.name}</h3>
                {c.client_name && <p className="text-xs text-slate-400">Client: {c.client_name}</p>}
                <div className="mt-1 text-xs text-slate-400 space-y-0.5">{c.objective && <p>Objective: {c.objective}</p>}{c.budget && <p>Budget: {c.budget}</p>}</div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Badge className="bg-primary/20 text-primary">{c.status}</Badge>
                <Button size="icon" variant="ghost" className="text-red-400 h-8 w-8" onClick={() => del(c.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-slate-500 text-sm">No campaigns yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit campaign" : "New campaign"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name} onChange={set("name")} required data-testid="campaign-form-name" /></div>
            <div className="space-y-1.5"><Label>Client ID (optional)</Label><Input value={form.client_id} onChange={set("client_id")} placeholder="Link to a client user id" /></div>
            <div className="space-y-1.5"><Label>YouTube URL</Label><Input value={form.youtube_url} onChange={set("youtube_url")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Objective</Label>
                <Select value={form.objective} onValueChange={set("objective")}><SelectTrigger data-testid="campaign-objective"><SelectValue placeholder="Select objective" /></SelectTrigger><SelectContent>{OBJECTIVES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Ad provider</Label>
                <Select value={form.provider} onValueChange={set("provider")}><SelectTrigger data-testid="campaign-provider"><SelectValue placeholder="Select platform" /></SelectTrigger><SelectContent>{PROVIDERS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Destination URL / Landing page</Label><Input value={form.destination_url} onChange={set("destination_url")} placeholder="https://..." /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Daily budget</Label><Input value={form.daily_budget} onChange={set("daily_budget")} /></div>
              <div className="space-y-1.5"><Label>Total budget</Label><Input value={form.total_budget} onChange={set("total_budget")} /></div>
              <div className="space-y-1.5"><Label>Countries</Label><Input value={form.target_countries} onChange={set("target_countries")} placeholder="US, IN, UK" data-testid="campaign-countries" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Ad headline</Label><Input value={form.headline} onChange={set("headline")} /></div>
              <div className="space-y-1.5"><Label>Call to action</Label><Input value={form.cta} onChange={set("cta")} placeholder="Learn More" /></div>
            </div>
            <div className="space-y-1.5"><Label>Ad description</Label><Textarea value={form.description} onChange={set("description")} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Target country</Label><Input value={form.target_country} onChange={set("target_country")} /></div>
              <div className="space-y-1.5"><Label>Target audience</Label><Input value={form.target_audience} onChange={set("target_audience")} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Start date</Label><Input type="date" value={form.start_date?.slice(0,10) || ""} onChange={set("start_date")} /></div>
              <div className="space-y-1.5"><Label>End date</Label><Input type="date" value={form.end_date?.slice(0,10) || ""} onChange={set("end_date")} /></div>
            </div>
            <div className="space-y-1.5"><Label>Status</Label><Select value={form.status} onValueChange={set("status")}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Notes (visible to client)</Label><Textarea value={form.notes} onChange={set("notes")} rows={3} /></div>
            <DialogFooter className="gap-2 sm:gap-2">
              {editId && <Button type="button" variant="outline" onClick={() => submitToProvider(editId)} data-testid="campaign-submit-provider">Submit to provider</Button>}
              <Button type="submit" disabled={loading} data-testid="campaign-form-save">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
